import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled from 'styled-components';

import {hoverSelector} from '../../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {FrontEvent, Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {LegacyNotificationModel} from '../../../../../models/api/legacyNotificationModel';
import {InteractiveDateTimeFormatterContainer} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatterContainer';
import {Link} from '../../../../common/links/link';

/*
 * Props.
 */

export interface NotificationCellBasePublicProps {
  notification: LegacyNotificationModel;
  onRequestClose?: Handler;
  onToggleMarkAsReadClick?: Handler;
}

interface NotificationCellBaseProps extends NotificationCellBasePublicProps {
  icon: ReactNode;
  title: ReactNode;
}

/*
 * Style.
 */

interface NotificationStyleProps {
  $isUnread: boolean;
}

const StyledGridDiv = styled.div<NotificationStyleProps>`
  display: grid;
  grid-template-columns: 18px 1fr auto;
  grid-template-rows: auto auto;
  grid-column-gap: 6px;
  grid-row-gap: 6px;
  grid-template-areas:
    'left top     top-right'
    'left content content';

  align-items: center;
  padding: 12px;

  color: ${(p) => p.theme.greys.shade80};
  background-color: ${(p) => (p.$isUnread ? p.theme.palette.blue.shade10 : p.theme.foreground)};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledDotButton = styled(ChromelessButton)`
  grid-area: left;
  padding: 4px;
`;

const StyledDotDiv = styled.div<NotificationStyleProps>`
  position: relative;
  width: 10px;
  height: 10px;

  border-radius: 5px;
  background-color: ${(p) => p.theme.palette.blue.shade40};

  opacity: ${(p) => (p.$isUnread ? 1 : 0)};
  ${hoverSelector} {
    opacity: ${(p) => (p.$isUnread ? 1 : 0.7)};
  }
`;

const StyledTopDiv = styled.div`
  grid-area: top;
  display: flex;
  align-items: center;
  min-width: 0;
`;

const StyledIconDiv = styled.div`
  flex-shrink: 0;
  margin-right: 6px;
`;

const StyledTitleDiv = styled.div`
  flex: 1;
  color: ${(p) => p.theme.greys.shade90};
  ${ellipsis()};
`;

const StyledDateDiv = styled.div`
  grid-area: top-right;

  text-transform: uppercase;
  font-size: ${fontSizes.tiny};
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledContentDiv = styled.div`
  grid-area: content;
  max-height: 60px;

  color: ${(p) => p.theme.greys.shade70};
  line-height: 20px;

  overflow: hidden;
`;

/*
 * Component.
 */

export const NotificationCellBase: FC<PropsWithChildren<NotificationCellBaseProps>> = (props) => {
  const {notification, onRequestClose, icon, title, children} = props;
  const notificationLink = notification.p2FiltersLink;

  return (
    <Link to={notificationLink} onClick={() => onRequestClose && onRequestClose()}>
      <StyledGridDiv $isUnread={notification.isUnread}>
        <StyledDotButton onClick={(e) => onToggleMarkAsReadClick(e, props)}>
          <StyledDotDiv $isUnread={notification.isUnread} />
        </StyledDotButton>
        <StyledTopDiv>
          <StyledIconDiv>{icon}</StyledIconDiv>
          <StyledTitleDiv>{title}</StyledTitleDiv>
        </StyledTopDiv>
        <StyledDateDiv>
          <InteractiveDateTimeFormatterContainer value={notification.bumpedAt} />
        </StyledDateDiv>
        <StyledContentDiv>{children}</StyledContentDiv>
      </StyledGridDiv>
    </Link>
  );
};

function onToggleMarkAsReadClick(e: FrontEvent, props: NotificationCellBaseProps) {
  e.preventDefault();
  e.stopPropagation();
  return props.onToggleMarkAsReadClick && props.onToggleMarkAsReadClick();
}
